import { displayPercentage } from "@/domains/display/display-percentage"
import { computed, makeObservable, observable } from "mobx"

import { dollars, prettifyNumber } from "@/domains/library"

import type { ScaleStatsStoreDAO } from "./types"

export class ScaleStatsStore {
  // Incentives data
  totalIncentives: number = 0
  incentivesPerVote: number = 0
  totalIncentivesLastEpoch: number = 0
  incentivesPerVoteLastEpoch: number = 0

  // Basic stats
  blockTimestamp: number = 0
  price: number = 0
  priceFtm: number = 0
  priceInFtm: number = 0

  // Supply data
  circulatingSupply: number = 0
  outstandingSupply: number = 0
  dilutedSupply: number = 0
  inNFT: number = 0
  inGauges: number = 0
  inExcluded: number = 0
  veTotalSupply: number = 0

  // Financial metrics
  lockRatio: number = 0
  liquidity: number = 0
  circulatingMarketCap: number = 0
  marketCap: number = 0
  fdv: number = 0
  lockedMarketCap: number = 0
  totalTvl: number = 0

  // Ecosystem data
  ecosystemNumTokens: number = 0
  ecosystemNumPairs: number = 0
  ecosystemNumGauges: number = 0

  constructor(storeDAO?: ScaleStatsStoreDAO) {
    if (storeDAO) {
      // Incentives data (direct mapping from flattened DAO)
      this.totalIncentives = storeDAO.totalIncentives
      this.incentivesPerVote = storeDAO.incentivesPerVote
      this.totalIncentivesLastEpoch = storeDAO.totalIncentivesLastEpoch
      this.incentivesPerVoteLastEpoch = storeDAO.incentivesPerVoteLastEpoch

      // Basic stats
      this.blockTimestamp = storeDAO.blockTimestamp
      this.price = storeDAO.price
      this.priceFtm = storeDAO.priceFtm
      this.priceInFtm = storeDAO.priceInFtm

      // Supply data
      this.circulatingSupply = storeDAO.circulatingSupply
      this.outstandingSupply = storeDAO.outstandingSupply
      this.dilutedSupply = storeDAO.dilutedSupply
      this.inNFT = storeDAO.inNFT
      this.inGauges = storeDAO.inGauges
      this.inExcluded = storeDAO.inExcluded
      this.veTotalSupply = storeDAO.veTotalSupply

      // Financial metrics
      this.lockRatio = storeDAO.lockRatio
      this.liquidity = storeDAO.liquidity
      this.circulatingMarketCap = storeDAO.circulatingMarketCap
      this.marketCap = storeDAO.marketCap
      this.fdv = storeDAO.fdv
      this.lockedMarketCap = storeDAO.lockedMarketCap
      this.totalTvl = storeDAO.totalTvl

      // Ecosystem data (direct mapping from flattened DAO)
      this.ecosystemNumTokens = storeDAO.ecosystemNumTokens
      this.ecosystemNumPairs = storeDAO.ecosystemNumPairs
      this.ecosystemNumGauges = storeDAO.ecosystemNumGauges
    }

    makeObservable(this, {
      // Incentives data
      totalIncentives: observable,
      incentivesPerVote: observable,
      totalIncentivesLastEpoch: observable,
      incentivesPerVoteLastEpoch: observable,

      // Basic stats
      blockTimestamp: observable,
      price: observable,
      priceFtm: observable,
      priceInFtm: observable,

      // Supply data
      circulatingSupply: observable,
      outstandingSupply: observable,
      dilutedSupply: observable,
      inNFT: observable,
      inGauges: observable,
      inExcluded: observable,
      veTotalSupply: observable,

      // Financial metrics
      lockRatio: observable,
      liquidity: observable,
      circulatingMarketCap: observable,
      marketCap: observable,
      fdv: observable,
      lockedMarketCap: observable,
      totalTvl: observable,

      // Ecosystem data
      ecosystemNumTokens: observable,
      ecosystemNumPairs: observable,
      ecosystemNumGauges: observable,

      // Computed properties - only for calculated values
      veApr: computed,
    })
  }

  // Update the stats data (called by orchestrator)
  updateData(newData: ScaleStatsStoreDAO): void {
    // Incentives data (direct mapping from flattened DAO)
    this.totalIncentives = newData.totalIncentives
    this.incentivesPerVote = newData.incentivesPerVote
    this.totalIncentivesLastEpoch = newData.totalIncentivesLastEpoch
    this.incentivesPerVoteLastEpoch = newData.incentivesPerVoteLastEpoch

    // Basic stats
    this.blockTimestamp = newData.blockTimestamp
    this.price = newData.price
    this.priceFtm = newData.priceFtm
    this.priceInFtm = newData.priceInFtm

    // Supply data
    this.circulatingSupply = newData.circulatingSupply
    this.outstandingSupply = newData.outstandingSupply
    this.dilutedSupply = newData.dilutedSupply
    this.inNFT = newData.inNFT
    this.inGauges = newData.inGauges
    this.inExcluded = newData.inExcluded
    this.veTotalSupply = newData.veTotalSupply

    // Financial metrics
    this.lockRatio = newData.lockRatio
    this.liquidity = newData.liquidity
    this.circulatingMarketCap = newData.circulatingMarketCap
    this.marketCap = newData.marketCap
    this.fdv = newData.fdv
    this.lockedMarketCap = newData.lockedMarketCap
    this.totalTvl = newData.totalTvl

    // Ecosystem data (direct mapping from flattened DAO)
    this.ecosystemNumTokens = newData.ecosystemNumTokens
    this.ecosystemNumPairs = newData.ecosystemNumPairs
    this.ecosystemNumGauges = newData.ecosystemNumGauges
  }

  calculateBribeROI(emissions: number): number {
    const emissionsValue = emissions * this.price
    const bribeROI = this.totalIncentives > 0 ? emissionsValue / this.totalIncentives : 0
    return bribeROI
  }

  /**
   * APR uses decimal values of 0.015 value to mean 1.5%
   */
  get veApr(): number {
    const price = this.price
    const incentivesPerVote = this.incentivesPerVote
    const veApr = price ? (incentivesPerVote * 52) / price : 0
    return veApr
  }

  /**
   * Display the circulating market cap in a human readable format
   * @param decimals
   * @returns
   */
  displayCirculatingMarketCap(decimals = 2): string {
    return prettifyNumber(this.circulatingMarketCap, decimals).toLowerCase()
  }

  displayIncentivesPerVote(decimals = 6) {
    return dollars(this.incentivesPerVote, decimals)
  }

  /**
   * Display the lock ratio in a human readable format
   * @param decimals
   * @returns
   */
  displayLockRatio(decimals = 2): string {
    return displayPercentage(this.lockRatio, decimals)
  }

  displayNumberOfPairs(): string {
    return prettifyNumber(this.ecosystemNumPairs, 0).toLowerCase()
  }

  displayNumberOfTokens(): string {
    return prettifyNumber(this.ecosystemNumTokens, 0).toLowerCase()
  }

  displayTotalIncentives(decimals = 2) {
    return dollars(this.totalIncentives, decimals)
  }

  /**
   * Display the veAPR in a human readable format
   * @param decimals
   * @returns
   */
  displayVeApr(decimals = 2): string {
    return displayPercentage(this.veApr, decimals)
  }

  /**
   * Display the total TVL in a human readable format
   * @returns
   */
  displayTotalTvl(): string {
    return prettifyNumber(this.totalTvl).toLowerCase()
  }
}
